import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import api from "../api";
import { Activity, Sparkles, Building2, Zap, Flame, Globe, Filter, Pause, Play } from "lucide-react";
import { findCountry, flagOf } from "./countries";

/**
 * 2D World map dashboard.
 *  - Choropleth: countries with active users glow (Layer 1).
 *  - Event flashes (Layer 2): pulse for registrations, gold for merchants, lightning for tx.
 *  - Cross-country tx → curved comet arcs (Idea A).
 *  - Optional heatmap layer (Idea B).
 *  - Side pulse feed (Idea C).
 *  - Layer toggles (Idea D).
 *  - Polls every 15s.
 *
 * Uses world atlas hosted by react-simple-maps' canonical CDN (TopoJSON 110m).
 */
const WORLD_TOPO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/* ISO numeric → ISO-2 mapping for the world atlas (geography.id = numeric code). */
const NUM_TO_ISO2 = {
  "004":"AF","008":"AL","012":"DZ","020":"AD","024":"AO","028":"AG","031":"AZ","032":"AR","036":"AU","040":"AT",
  "044":"BS","048":"BH","050":"BD","051":"AM","052":"BB","056":"BE","068":"BO","070":"BA","072":"BW","076":"BR",
  "084":"BZ","096":"BN","100":"BG","104":"MM","108":"BI","112":"BY","116":"KH","120":"CM","124":"CA","132":"CV",
  "140":"CF","144":"LK","148":"TD","152":"CL","156":"CN","158":"TW","170":"CO","174":"KM","178":"CG","180":"CD",
  "188":"CR","191":"HR","192":"CU","196":"CY","203":"CZ","204":"BJ","208":"DK","212":"DM","214":"DO","218":"EC",
  "222":"SV","226":"GQ","231":"ET","232":"ER","233":"EE","242":"FJ","246":"FI","250":"FR","262":"DJ","266":"GA",
  "268":"GE","270":"GM","275":"PS","276":"DE","288":"GH","296":"KI","300":"GR","308":"GD","320":"GT","324":"GN",
  "328":"GY","332":"HT","340":"HN","344":"HK","348":"HU","352":"IS","356":"IN","360":"ID","364":"IR","368":"IQ",
  "372":"IE","376":"IL","380":"IT","384":"CI","388":"JM","392":"JP","398":"KZ","400":"JO","404":"KE","408":"KP",
  "410":"KR","414":"KW","417":"KG","418":"LA","422":"LB","426":"LS","428":"LV","430":"LR","434":"LY","438":"LI",
  "440":"LT","442":"LU","450":"MG","454":"MW","458":"MY","462":"MV","466":"ML","470":"MT","478":"MR","480":"MU",
  "484":"MX","492":"MC","496":"MN","498":"MD","499":"ME","504":"MA","508":"MZ","512":"OM","516":"NA","520":"NR",
  "524":"NP","528":"NL","540":"NC","548":"VU","554":"NZ","558":"NI","562":"NE","566":"NG","578":"NO","583":"FM",
  "584":"MH","585":"PW","586":"PK","591":"PA","598":"PG","600":"PY","604":"PE","608":"PH","616":"PL","620":"PT",
  "624":"GW","626":"TL","630":"PR","634":"QA","642":"RO","643":"RU","646":"RW","659":"KN","662":"LC","670":"VC",
  "678":"ST","682":"SA","686":"SN","688":"RS","690":"SC","694":"SL","702":"SG","703":"SK","704":"VN","705":"SI",
  "706":"SO","710":"ZA","716":"ZW","724":"ES","728":"SS","729":"SD","740":"SR","748":"SZ","752":"SE","756":"CH",
  "760":"SY","762":"TJ","764":"TH","768":"TG","776":"TO","780":"TT","784":"AE","788":"TN","792":"TR","795":"TM",
  "798":"TV","800":"UG","804":"UA","807":"MK","818":"EG","826":"GB","834":"TZ","840":"US","854":"BF","858":"UY",
  "860":"UZ","862":"VE","882":"WS","887":"YE","894":"ZM",
};

const POLL_MS = 15000;
const FLASH_DURATION_MS = 3000;
const MAP_W = 980;
const MAP_H = 520;

export function WorldMapDashboard() {
  const [snap, setSnap] = useState({ countries: [], stats: { activeCountries: 0, totalUsers: 0, totalMerchants: 0 } });
  const [events, setEvents] = useState([]);
  const [pulse, setPulse] = useState([]);
  const [paused, setPaused] = useState(false);
  const [layers, setLayers] = useState({ registrations: true, merchants: true, transactions: true, arcs: true, heatmap: false });
  const lastEventTs = useRef("");
  const [activeFlashes, setActiveFlashes] = useState([]); // {id, lat, lng, type, toLat?, toLng?, ts}

  // Country → users lookup for choropleth fill
  const byCC = useMemo(() => {
    const m = {};
    snap.countries.forEach((c) => { m[c.code] = c; });
    return m;
  }, [snap.countries]);
  const maxUsers = useMemo(() => Math.max(1, ...snap.countries.map((c) => c.users)), [snap.countries]);

  // Build choropleth color from user count (dark base → cyan glow)
  const fillFor = (cc) => {
    const c = byCC[cc];
    if (!c || c.users === 0) return "#161620";
    const intensity = Math.min(1, Math.log(c.users + 1) / Math.log(maxUsers + 1));
    // Blend rgba(0,242,255) over #1a1a3a
    const r = Math.round(26 + (0 - 26) * intensity);
    const g = Math.round(26 + (242 - 26) * intensity);
    const b = Math.round(58 + (255 - 58) * intensity);
    return `rgb(${r},${g},${b})`;
  };

  // Poll snapshot + events every 15s, also catches up missed flashes.
  const load = async () => {
    try {
      const [s, e, p] = await Promise.all([
        api.get("/map/snapshot"),
        api.get("/map/events?limit=20"),
        api.get("/map/pulse?limit=20"),
      ]);
      setSnap(s.data || { countries: [], stats: {} });
      setEvents(e.data?.events || []);
      setPulse(p.data?.pulse || []);
      // Detect NEW events since last poll → push as flashes
      const all = e.data?.events || [];
      const newer = lastEventTs.current
        ? all.filter((x) => (x.at || "") > lastEventTs.current)
        : all.slice(0, 6); // initial pop: show last 6 flashes
      if (all.length && all[0].at) lastEventTs.current = all[0].at;
      if (newer.length && !paused) {
        const flashes = newer.map((x, i) => ({
          id: `${x.at || ""}-${i}-${Math.random()}`,
          type: x.type, lat: x.lat, lng: x.lng,
          toLat: x.toLat, toLng: x.toLng,
          ts: Date.now() + i * 200, // stagger
        }));
        setActiveFlashes((cur) => [...cur, ...flashes].slice(-30));
        // Auto-cleanup
        setTimeout(() => {
          setActiveFlashes((cur) => cur.filter((f) => !flashes.find((nf) => nf.id === f.id)));
        }, FLASH_DURATION_MS + flashes.length * 200);
      }
    } catch { /* keep last */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(load, POLL_MS);
    // Pause polling when the tab is hidden to save bandwidth & DB load.
    const onVis = () => { if (document.hidden) clearInterval(id); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line
  }, [paused]);

  // Projection used to convert lat/lng → svg coords for flash overlay.
  const projection = useMemo(
    () => geoMercator().scale(140).translate([MAP_W / 2, MAP_H / 1.55]),
    []
  );
  const project = (lat, lng) => {
    const pt = projection([lng, lat]);
    return pt ? { x: pt[0], y: pt[1] } : null;
  };

  // Filter flashes by toggles
  const visibleFlashes = activeFlashes.filter((f) => {
    if (f.type === "registration" && !layers.registrations) return false;
    if (f.type === "merchant" && !layers.merchants) return false;
    if (f.type === "transaction" && !layers.transactions) return false;
    return true;
  });

  return (
    <section className="tg-page" style={{ padding: "12px 0 48px" }} data-testid="world-map-dashboard">
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 320px", gap: 18, alignItems: "start" }}
           className="map-grid">
        {/* MAP */}
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(135deg, #06060a, #0e0e18 70%)", borderColor: "rgba(0,242,255,0.18)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
            <div>
              <div className="tg-eyebrow" style={{ color: "#00F2FF" }}><Globe size={12} /> Live Map · Реальное время</div>
              <h2 className="tg-h2" style={{ margin: "8px 0 2px", fontSize: 22 }}>Глобальная активность TGC</h2>
              <div style={{ fontSize: 12, color: "var(--tg-text-hint)" }}>
                {snap.stats?.activeCountries || 0} стран · {(snap.stats?.totalUsers || 0).toLocaleString("ru-RU")} участников · {(snap.stats?.totalMerchants || 0).toLocaleString("ru-RU")} мерчантов · обновляется каждые 15 сек
                {snap.stats && snap.stats.totalUsers > (snap.stats.usersOnMap || snap.stats.totalUsers) && (
                  <span style={{ marginLeft: 8, opacity: 0.65 }}>
                    · {snap.stats.totalUsers - (snap.stats.usersOnMap || 0)} с устаревшим кодом страны
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => setPaused(!paused)} data-testid="map-pause-toggle"
              style={{ padding: "7px 12px", borderRadius: 10, border: "1px solid var(--tg-border)", background: "transparent", color: paused ? "#FF3D6C" : "#10F4A7", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {paused ? <Play size={12} /> : <Pause size={12} />} {paused ? "Возобновить" : "Пауза"}
            </button>
          </div>

          {/* Layer toggles */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }} data-testid="map-layers">
            {[
              ["registrations", "👤 Регистрации", "#00F2FF"],
              ["merchants",     "🏪 Мерчанты",    "#FFD700"],
              ["transactions",  "⚡ Транзакции",  "#10F4A7"],
              ["arcs",          "🌐 Дуги",         "#BC13FE"],
              ["heatmap",       "🔥 Heatmap",     "#FF6B35"],
            ].map(([key, lbl, col]) => (
              <button key={key} onClick={() => setLayers((l) => ({ ...l, [key]: !l[key] }))}
                data-testid={`map-layer-${key}`}
                style={{
                  padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  border: `1px solid ${layers[key] ? col : "var(--tg-border)"}`,
                  background: layers[key] ? `${col}22` : "transparent",
                  color: layers[key] ? col : "var(--tg-text-hint)",
                  transition: "all .15s",
                }}>{lbl}</button>
            ))}
          </div>

          <div style={{ position: "relative", width: "100%", aspectRatio: `${MAP_W} / ${MAP_H}`, overflow: "hidden", borderRadius: 12, background: "#06060a" }}>
            <ComposableMap width={MAP_W} height={MAP_H} projection="geoMercator"
              projectionConfig={{ scale: 140, center: [0, 20] }}
              style={{ width: "100%", height: "100%" }}>
              <ZoomableGroup minZoom={1} maxZoom={6} center={[0, 20]}>
                <Geographies geography={WORLD_TOPO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const cc = NUM_TO_ISO2[String(geo.id).padStart(3, "0")] || "";
                      const active = !!byCC[cc];
                      return (
                        <Geography key={geo.rsmKey} geography={geo}
                          fill={fillFor(cc)}
                          stroke={active ? "#00F2FF" : "#1c1c28"}
                          strokeWidth={active ? 0.6 : 0.3}
                          style={{
                            default: { outline: "none" },
                            hover: { fill: active ? "#00F2FF55" : "#252535", outline: "none", cursor: "default" },
                            pressed: { outline: "none" },
                          }} />
                      );
                    })
                  }
                </Geographies>

                {/* Heatmap layer (filled glowing circles per country, sized by users) */}
                {layers.heatmap && snap.countries.map((c) => {
                  if (!c.lat || !c.lng) return null;
                  const pt = project(c.lat, c.lng);
                  if (!pt) return null;
                  const intensity = Math.min(1, Math.log(c.users + 1) / Math.log(maxUsers + 1));
                  const radius = 4 + intensity * 22;
                  return (
                    <g key={`hm-${c.code}`} style={{ pointerEvents: "none" }}>
                      <circle cx={pt.x} cy={pt.y} r={radius} fill="url(#hm-grad)" opacity={0.65} />
                    </g>
                  );
                })}

                {/* Cross-country arcs */}
                {layers.arcs && visibleFlashes.filter((f) => f.type === "transaction" && f.toLat).map((f) => {
                  const from = project(f.lat, f.lng);
                  const to = project(f.toLat, f.toLng);
                  if (!from || !to) return null;
                  const dx = to.x - from.x, dy = to.y - from.y;
                  const mx = (from.x + to.x) / 2, my = (from.y + to.y) / 2 - Math.abs(dx) * 0.25 - 20;
                  const d = `M${from.x},${from.y} Q${mx},${my} ${to.x},${to.y}`;
                  return (
                    <g key={`arc-${f.id}`} style={{ pointerEvents: "none" }}>
                      <path d={d} fill="none" stroke="#BC13FE" strokeWidth={1.2} opacity={0.85}
                        strokeDasharray="200" strokeDashoffset="200"
                        style={{ animation: "wm-arc 1.6s ease-out forwards" }} />
                    </g>
                  );
                })}

                {/* Flashes */}
                {visibleFlashes.map((f) => {
                  const pt = project(f.lat, f.lng);
                  if (!pt) return null;
                  const col = f.type === "registration" ? "#00F2FF" : f.type === "merchant" ? "#FFD700" : "#10F4A7";
                  return (
                    <g key={f.id} style={{ pointerEvents: "none" }}>
                      <circle cx={pt.x} cy={pt.y} r="2" fill={col} opacity="1" />
                      <circle cx={pt.x} cy={pt.y} r="3" fill={col} opacity="0.7"
                        style={{ animation: "wm-pulse 2.6s ease-out forwards", transformOrigin: `${pt.x}px ${pt.y}px` }} />
                      {f.type === "merchant" && (
                        <rect x={pt.x - 4} y={pt.y - 4} width="8" height="8" fill="none" stroke={col} strokeWidth="1"
                          style={{ animation: "wm-pulse 2.6s ease-out forwards", transformOrigin: `${pt.x}px ${pt.y}px` }} />
                      )}
                      {f.type === "transaction" && (
                        <polyline points={`${pt.x - 2},${pt.y - 6} ${pt.x + 1},${pt.y - 1} ${pt.x - 1},${pt.y + 1} ${pt.x + 2},${pt.y + 6}`}
                          fill="none" stroke={col} strokeWidth="1.4"
                          style={{ animation: "wm-flash 0.9s ease-out forwards" }} />
                      )}
                    </g>
                  );
                })}

                <defs>
                  <radialGradient id="hm-grad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#FFD700" stopOpacity="0.95" />
                    <stop offset="50%" stopColor="#FF6B35" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#FF3D6C" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </ZoomableGroup>
            </ComposableMap>
          </div>

          {/* Countries summary — ALL active countries, sorted by user count desc */}
          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", maxHeight: 180, overflowY: "auto" }} data-testid="map-top-countries">
            {[...snap.countries].sort((a, b) => b.users - a.users).map((c) => {
              const isLegacy = c.legacy || !c.lat;
              return (
                <div key={c.code} style={{
                  padding: "6px 12px", borderRadius: 999,
                  background: isLegacy ? "rgba(255,255,255,0.04)" : "rgba(0,242,255,0.08)",
                  border: `1px solid ${isLegacy ? "rgba(255,255,255,0.18)" : "rgba(0,242,255,0.25)"}`,
                  display: "flex", alignItems: "center", gap: 6,
                  fontSize: 12, fontWeight: 700, color: "var(--tg-text)",
                  opacity: isLegacy ? 0.75 : 1,
                }} title={isLegacy ? "Устаревший код страны, не отображается на карте" : ""}>
                  <span style={{ fontSize: 14 }}>{isLegacy ? "🌍" : flagOf(c.code)}</span>
                  <span>{isLegacy ? "Другое" : findCountry(c.code).ru}</span>
                  <span style={{ color: isLegacy ? "var(--tg-text-hint)" : "#00F2FF" }}>{c.users}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* PULSE WIDGET */}
        <div className="tg-card" style={{ padding: 18, background: "linear-gradient(180deg, #0b0b14, #06060a)", borderColor: "rgba(255,215,0,0.2)" }} data-testid="map-pulse-widget">
          <div className="tg-eyebrow gold"><Activity size={12} /> Пульс платформы</div>
          <h3 className="tg-h2" style={{ fontSize: 18, margin: "8px 0 4px" }}>Live Feed</h3>
          <div style={{ fontSize: 11, color: "var(--tg-text-hint)", marginBottom: 14 }}>Последние события сети</div>
          <div style={{ display: "grid", gap: 8, maxHeight: 540, overflowY: "auto", paddingRight: 6 }} data-testid="map-pulse-list">
            {pulse.length === 0 && (
              <div style={{ color: "var(--tg-text-hint)", fontSize: 13, textAlign: "center", padding: 20 }}>
                Ожидание событий...
              </div>
            )}
            {pulse.map((p, idx) => (
              <div key={idx} style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                padding: "8px 10px", borderRadius: 10,
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${p.color}33`,
                animation: idx === 0 ? "wm-feed-in .5s" : "none",
              }} data-testid={`pulse-item-${idx}`}>
                <div style={{ fontSize: 18, lineHeight: 1, color: p.color }}>{p.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "var(--tg-text)", lineHeight: 1.4 }}>{p.text}</div>
                  <div style={{ fontSize: 10, color: "var(--tg-text-hint)", marginTop: 2 }}>
                    {p.at ? new Date(p.at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes wm-pulse {
          0%   { transform: scale(0.8); opacity: 0.95; }
          70%  { transform: scale(8);   opacity: 0.0; }
          100% { transform: scale(10);  opacity: 0; }
        }
        @keyframes wm-flash {
          0%   { opacity: 0; transform: translateY(2px); }
          40%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-2px); }
        }
        @keyframes wm-arc {
          to { stroke-dashoffset: 0; }
        }
        @keyframes wm-feed-in {
          0% { transform: translateX(8px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @media (max-width: 980px) {
          .map-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
